import React from 'react';
import Router from 'next/router';
import styled from '@emotion/styled';
import { Badge, Box, Flex, Hide } from '@qga/roo-ui/components';
import PropTypes from 'prop-types';
import { isEmpty } from 'ramda';
import { themeGet } from 'styled-system';

import { BEST_RATE_ID } from '@shared/v2/constants/packages';
import { useDataLayerContext } from '@shared/v2/contexts/DataLayerContext';

import Button from '@/components/Button';
import { propertyCardCtaDataLayerEvent } from '@/components/DestinationPage/utils/dataLayer';
import Divider from '@/components/Divider/Divider';
import isNotNilOrEmpty from '@/lib/object/isNotNilOrEmpty';
import ImageGallery from '@/shared/components/ImageGallery';
import getPropertyLink from '@/utils/getPropertyLink';
import PropertyTitle from '@/v2/components/ui/PropertyTitle';
import { usePackagePageV2Analytics } from '@/v2/hooks/featureFlags';
import { useGA4Events } from '@/v2/hooks/featureFlags/ga4Events/useGA4Events';
import useSelectItemEvent from '@/v2/hooks/useSelectItemEvent';
import { dataLayer } from '@/v2/utils/dataLayer';
import PackageOffer from '../../../../../../propTypes/PackageOffer';
import Property from '../../../../../../propTypes/Property';
import SearchQuery from '../../../../../../propTypes/SearchQuery';
import Link from '../../../../../Link';
import PackageHeroSummary from '../../../../../PackageHeroSummary';
import PricePerTraveller from '../../../../../PricePerTraveller';
import withSkeleton from '../../../../../Skeleton/withSkeleton';

const StyledPricePerTraveller = styled.div`
  color: ${themeGet('colors.primary')};
`;

const PropertyOption = ({
  searchQuery,
  propertyContent,
  Skeleton,
  packageOffer,
  priceForAdult = false,
  hidePriceSubText = false,
  flightBadgeText,
  campaignPromoCode,
  index,
}) => {
  const shouldUseNewEvents = useGA4Events();
  const { images } = propertyContent || {};
  const { charges } = packageOffer || {};
  const { total, perAdult, perTraveller } = charges || {};
  const hasOffer = isNotNilOrEmpty(searchQuery) && isNotNilOrEmpty(charges);
  const hasSearchQuery = searchQuery && !isEmpty(searchQuery);
  const perTravellerRate = priceForAdult ? perAdult : perTraveller;

  const hideCampaignOnPackagePage =
    !!campaignPromoCode && !flightBadgeText && hasSearchQuery;

  const selectItemEventData = useSelectItemEvent({
    property: { propertyContent },
    destination: propertyContent.destination.title,
    name: `${propertyContent.destination.title} Holidays`,
    price: packageOffer?.charges?.total?.amount,
    itemVariant: packageOffer?.roomTypeName,
    index,
  });

  const href = getPropertyLink({
    propertyContent,
    packageOffer,
    searchQuery,
    noCampaignPackageOptions: hideCampaignOnPackagePage,
    packageOption: BEST_RATE_ID,
  });

  const shouldUseNewAnalytics = usePackagePageV2Analytics();
  const { pageName } = useDataLayerContext() || {};
  const ctaLabel = hasSearchQuery ? 'Select package' : 'Check availability';

  const onCtaClick = () => {
    if (ctaLabel && pageName && shouldUseNewAnalytics) {
      propertyCardCtaDataLayerEvent({ pageName, actionName: ctaLabel });
      if (shouldUseNewEvents) {
        dataLayer.selectItemEvent(selectItemEventData);
        dataLayer.ctaClickEvent({
          itemText: 'Select package',
          url: href?.pathname,
        });
      }
    }
  };

  return (
    <Flex
      data-testid="PROPERTY_OPTION"
      bg="background.card"
      flexDirection={['column', null, 'row']}
      boxShadow="light"
      borderRadius="md"
      border="1"
      borderColor="lightGrey"
      overflow="hidden"
    >
      <Box
        height={['200px', null, '420px']}
        minHeight={[null, null, '393px']}
        width={[null, null, '660px']}
        position="relative"
      >
        {flightBadgeText && (
          <Box position="absolute" zIndex="1" right={16} top={16}>
            <Badge
              data-testid="BADGE"
              text={flightBadgeText + '^'}
              fontWeight="700"
              fontSize="xxs"
              backgroundColor="ui.badgeBackground"
              padding="4px 8px"
            ></Badge>
          </Box>
        )}
        <Skeleton height="100%" display="block" borderRadius="0">
          <ImageGallery
            images={images}
            height={['200px', null, '420px']}
            mediaCounterStyle="counter"
            onClick={() => Router.push(href).then(() => window.scroll(0, 0))}
          />
        </Skeleton>
      </Box>
      <Flex
        pt={['4', null, '6']}
        pb="6"
        flexDirection="column"
        justifyContent="space-between"
        flex="1"
      >
        <Box px={['4', null, '6']}>
          <Skeleton width="60px" />
          <Skeleton>
            <Box mb="3">
              <Link.Naked href={href}>
                <PropertyTitle
                  destinationName={propertyContent.destination.title}
                  propertyName={propertyContent.name}
                  rating={propertyContent.rating}
                  ratingType={propertyContent.ratingType}
                  tags={propertyContent.tags}
                  tagline={propertyContent.tagline}
                />
              </Link.Naked>
            </Box>
          </Skeleton>
        </Box>
        {hasSearchQuery && (
          <Hide md lg>
            <Box px={['4', null, '6']} mb={4}>
              <Divider />
            </Box>
          </Hide>
        )}
        <Box px={['4', null, '6']}>
          <Skeleton width="30px">
            {hasSearchQuery && (
              <PackageHeroSummary searchQuery={searchQuery} exact showNights />
            )}
          </Skeleton>
          <Skeleton width="100px">
            {hasOffer && (
              <StyledPricePerTraveller mt="2">
                <PricePerTraveller
                  travellers={searchQuery}
                  total={total}
                  perTraveller={hidePriceSubText ? undefined : perTravellerRate}
                  priceForAdult={priceForAdult}
                />
              </StyledPricePerTraveller>
            )}
          </Skeleton>
          <Box mt={4} textAlign="right">
            <Skeleton type="Button" width="180px">
              {searchQuery && !hasOffer ? (
                <Button data-testid="CTA" outline disabled>
                  Not available
                </Button>
              ) : (
                <Link.Button
                  outline
                  onClick={onCtaClick}
                  data-testid="CTA"
                  width={['100%', null, '254px']}
                  href={href}
                >
                  {ctaLabel}
                </Link.Button>
              )}
            </Skeleton>
          </Box>
        </Box>
      </Flex>
    </Flex>
  );
};

PropertyOption.propTypes = {
  propertyContent: PropTypes.shape(Property).isRequired,
  packageOffer: PropTypes.shape(PackageOffer),
  Skeleton: PropTypes.func.isRequired,
  searchQuery: PropTypes.shape(SearchQuery),
  priceForAdult: PropTypes.bool,
  hidePriceSubText: PropTypes.bool,
  flightBadgeText: PropTypes.string,
  index: PropTypes.number.isRequired,
};

const PropertyOptionWithSkeleton = withSkeleton(({ loading }) => loading)(
  PropertyOption,
);

export default PropertyOptionWithSkeleton;
